/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */

import {
    closestCorners,
    getFirstCollision,
    KeyboardCode,
    DroppableContainer,
    KeyboardCoordinateGetter,
} from '@dnd-kit/core';
import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export const cn = (...inputs: ClassValue[]) => {
    return twMerge(clsx(inputs));
};

export const stripProtocol = (url: string) => {
    return url.replace(/(^\w+:|^)\/\//, '');
};

const directions: string[] = [KeyboardCode.Down, KeyboardCode.Up];

export const coordinateGetter: KeyboardCoordinateGetter = (
    event,
    { context: { active, droppableRects, droppableContainers, collisionRect } },
) => {
    if (directions.includes(event.code)) {
        event.preventDefault();

        if (!active || !collisionRect) {
            return;
        }

        const filteredContainers: DroppableContainer[] = [];

        droppableContainers.getEnabled().forEach((entry) => {
            if (!entry || entry?.disabled) {
                return;
            }

            const rect = droppableRects.get(entry.id);

            if (!rect) {
                return;
            }

            switch (event.code) {
                case KeyboardCode.Down:
                    if (collisionRect.top < rect.top) {
                        // find all droppable areas below
                        filteredContainers.push(entry);
                    }
                    break;
                case KeyboardCode.Up:
                    if (collisionRect.top > rect.top) {
                        // find all droppable areas above
                        filteredContainers.push(entry);
                    }
                    break;
            }
        });

        const collisions = closestCorners({
            active,
            collisionRect,
            droppableRects,
            droppableContainers: filteredContainers,
            pointerCoordinates: null,
        });
        const closestId = getFirstCollision(collisions, 'id');

        if (closestId !== null) {
            const newDroppable = droppableContainers.get(closestId);
            const newNode = newDroppable?.node.current;
            const newRect = newDroppable?.rect.current;

            if (newNode && newRect) {
                return {
                    x: newRect.left,
                    y: newRect.top,
                };
            }
        }
    }

    return undefined;
};

type DirtyFieldsType =
    | boolean
    | null
    | {
          [key: string]: DirtyFieldsType;
      }
    | DirtyFieldsType[];

export const getDirtyValues = <T extends Record<string, any>>(
    dirtyFields: Partial<Record<keyof T, DirtyFieldsType>>,
    values: T,
): Partial<T> => {
    const dirtyValues = Object.keys(dirtyFields).reduce((prev, key) => {
        const value = dirtyFields[key];
        if (!value) {
            return prev;
        }
        const isObject = typeof value === 'object';
        const isArray = Array.isArray(value);
        const nestedValue =
            isObject && !isArray ? getDirtyValues(value as Record<string, any>, values[key]) : values[key];
        return { ...prev, [key]: isArray ? values[key] : nestedValue };
    }, {} as Partial<T>);
    return dirtyValues;
};
